import { currentUser } from '@easy-expense/auth-client';
import {
  currentUserEntityFields,
  updateReportsEntities,
  getCachedReports,
  useCachedReports,
  useWorkspaceSettings,
} from '@easy-expense/data-firestore-client';
import { entityFields } from '@easy-expense/data-firestore-shared';
import { Category, Expense, ExpenseSchema } from '@easy-expense/data-schema-v2';
import Data from '@easy-expense/frontend-data-layer';
import { getTranslation, useIntlStore } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer, zIndex } from '@easy-expense/ui-web-core';
import { useIsExpenseApprovalLocked, useIsMember } from '@easy-expense/utils-client';
import {
  getDefaultReimbursableFields,
  getSplitApprovalFields,
  loadExpenseForForm,
  roundToCents,
} from '@easy-expense/utils-shared';
import { Checkbox } from '@mui/material';
import cuid from 'cuid';
import _ from 'lodash';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { DateField } from './DateField.component';
import { ExpenseTotalField } from './ExpenseTotalField.component';
import { NotesField } from './NotesField.component';
import { PaymentMethodField } from './PaymentMethodField.component';
import { ReceiptViewer } from './ReceiptViewer.component';
import { ReceiptsField } from './ReceiptsField.component';
import { ReportsField } from './ReportsField.component';
import {
  SplitExpenseModal,
  SplitExpenseForm as SplitExpenseFormValues,
} from './SplitExpenseModal.component';
import { TipField } from './TipField.component';
import { TransactionNumberField } from './TransactionNumberField.component';
import { useExpenseNavOrder } from '../../../hooks/useExpenseNavOrder';
import { isBlockingOverlayOpen, isEditableTarget } from '../../../lib/keyboard';
import { setResumeRow } from '../../../lib/resumeRow';
import { ApprovalStatusSection } from '../../Approvals/ApprovalStatusSection.component';
import { Button } from '../../Button.components';
import LoadingSpinner from '../../LoadingSpinner.component';
import { CloseButton } from '../../Shared/CloseButton.component';
import { SearchDropdown } from '../../Shared/Dropdown/SearchDropDown.component';
import { ExpenseNavControl } from '../ExpenseNavControl.component';
import { isTipEligibleCategoryKey } from '../helpers';

export const ExpenseEdit: React.FC<React.PropsWithChildren<{ expenseKey?: string }>> = ({
  expenseKey,
}) => {
  const storedExpense = Data.expenses.getByKey(expenseKey);
  const { currency: workspaceCurrency } = useIntlStore();
  // The stored expense as the form works with it: the workspace currency dropped as a foreign
  // one, receipt taxes rebuilt. Everything that reads or compares against the stored expense uses
  // this shape, so merely opening an expense never counts as a change.
  const loadedExpense = React.useMemo(() => {
    if (!storedExpense) {
      return undefined;
    }
    return loadExpenseForForm(storedExpense, workspaceCurrency);
  }, [storedExpense, workspaceCurrency]);
  const { workspaceSettings } = useWorkspaceSettings();
  // Reimbursable Default (EZ-5335): only a brand-new expense is pre-filled; an existing one keeps
  // whatever it was saved with.
  const [expense, setExpense] = React.useState<Partial<Expense>>(() => {
    if (loadedExpense) {
      return loadedExpense;
    }
    return {
      date: new Date().toISOString().substring(0, 10),
      ...getDefaultReimbursableFields(workspaceSettings),
    };
  });
  const expenseKeyRef = React.useRef<string | undefined>(expenseKey);
  const [reportKeys, _setReportKeys] = React.useState<string[]>([]);
  const [errors, setErrors] = React.useState<{ [k: string]: string[] }>();
  const allVendors = Data.vendors.use();
  const categories: Category[] = Data.expenseCategories.use();
  const categoryOptions = React.useMemo(
    () =>
      categories.map((category) => {
        const glCode = category.value.glCode;
        const displayName = glCode ? `${category.value.name} - ${glCode}` : category.value.name;
        return {
          key: category.key,
          value: {
            ...category.value,
            displayName,
          },
        };
      }),
    [categories],
  );

  const navigate = useNavigate();
  const location = useLocation();
  // Where to go when the expense is closed, saved, or deleted. Set by callers
  // that opened this expense from a transient view (e.g. a report's
  // Add/Remove Transactions picker) so we can return there instead of falling
  // back to browser-back / the Transactions tab.
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const [selectedReceiptIndex, setSelectedReceiptIndex] = React.useState<number>(0);
  const [loading, setLoading] = React.useState(false);
  const [renderKey, setRenderKey] = React.useState(cuid()); // used to rerender children
  const [newVendorName, setNewVendorName] = React.useState<string>();
  const [showSplitModal, setShowSplitModal] = React.useState(false);
  const screenWidth = window.innerWidth > 0 ? window.innerWidth : screen.width;
  const isMember = useIsMember();
  // Managers and admins stay editable here on purpose — mobile locks them and points
  // them at the web app to make the change.
  const isApprovalLocked = useIsExpenseApprovalLocked({
    expenseKey,
    approvalStatus: expense.approvalStatus,
  });
  const isLockedForMember = isMember && isApprovalLocked;
  const showTipField = isTipEligibleCategoryKey(expense.category);

  const navOrder = useExpenseNavOrder();
  const currentNavIndex = expenseKey ? navOrder.indexOf(expenseKey) : -1;

  // Opening an expense from the Transactions tab makes it the list's Resume Row,
  // so closing or saving returns to the page it sits on. Tracked here rather than
  // at the row click so paging with the arrows below moves the Resume Row too.
  // Expenses opened from a report or an approval flow carry a returnTo and must
  // leave the transactions list where it was.
  React.useEffect(() => {
    if (returnTo || !expenseKey) {
      return;
    }
    setResumeRow('transactions', expenseKey);
  }, [expenseKey, returnTo]);

  /**
   * Hand the Resume Row to the nearest surviving neighbour before an expense is
   * archived, so deleting from page six returns to page six rather than the
   * first page. Must run before the archive: the transactions table rewrites the
   * nav order the moment it re-mounts, taking the neighbours with it. Splitting
   * needs no equivalent — it updates the expense in place, so its key survives.
   */
  function passResumeRowToNeighbour() {
    if (returnTo || currentNavIndex < 0) {
      return;
    }
    setResumeRow('transactions', navOrder[currentNavIndex + 1] ?? navOrder[currentNavIndex - 1]);
  }

  // Report membership lives on the report, not the expense, so it has to be
  // compared separately from the expense document. useCachedReports returns
  // every report when the key is undefined, so a new expense starts with none.
  const savedReports = useCachedReports(expenseKey);
  const savedReportKeys = React.useMemo(
    () => (expenseKey ? savedReports.map((report) => report.key) : []),
    [savedReports, expenseKey],
  );

  // The form is dirty when the working copy differs from the stored expense, its
  // report membership has changed, or a brand-new vendor name has been typed but
  // not yet saved.
  const hasUnsavedChanges = React.useMemo(() => {
    if (!loadedExpense) {
      return false;
    }
    if (newVendorName) {
      return true;
    }
    if (!_.isEqual([...reportKeys].sort(), [...savedReportKeys].sort())) {
      return true;
    }
    return !_.isEqual(expense, loadedExpense);
  }, [expense, loadedExpense, newVendorName, reportKeys, savedReportKeys]);

  /**
   * Persists the current expense (create or update) and syncs its report
   * membership, without navigating away. Returns true on success, false if
   * validation fails (errors are surfaced on the form) or the write throws.
   */
  const persistExpense = React.useCallback((): boolean => {
    // The write itself reports `transaction_create` / `transaction_update` from
    // the entity store, with a full snapshot.
    setErrors({});

    let vendorKey = expense.vendor;
    if (newVendorName) {
      vendorKey = Data.vendors.create({ value: { name: newVendorName } });
    }

    if (!expense.date) {
      setErrors((errors) => ({ ...errors, date: ['invalid date'] }));
      return false;
    }

    try {
      const parsedExpense = ExpenseSchema.safeParse({
        ...expense,
        ...currentUserEntityFields('create'),
        vendor: vendorKey,
      });

      if (!parsedExpense.success) {
        setErrors((errors) => ({ ...errors, ...parsedExpense.error.formErrors.fieldErrors }));
        return false;
      }
    } catch (err) {
      console.error('error saving expense ', err);
      alert(getTranslation('Error saving expense. Please contact support.'));
      return false;
    }

    try {
      let resolvedExpenseKey = expenseKeyRef.current;
      if (expenseKeyRef.current) {
        Data.expenses.update(
          expenseKeyRef.current,
          { ...expense, vendor: vendorKey },
          undefined,
          undefined,
          'details',
        );
      } else {
        resolvedExpenseKey = Data.expenses.create(
          { ...expense, vendor: vendorKey },
          undefined,
          undefined,
          'details',
        );
      }

      if (!resolvedExpenseKey) {
        return false;
      }

      expenseKeyRef.current = resolvedExpenseKey;

      // The vendor now exists, so adopt its key and drop the pending name.
      // Leaving the name set would keep the form permanently dirty and re-create
      // the vendor on the next save - including the save that arrow navigation
      // triggers, which would stamp this vendor onto the next expense.
      if (newVendorName) {
        setNewVendorName(undefined);
        setExpense((expense) => ({ ...expense, vendor: vendorKey }));
      }

      const oldReports = getCachedReports(resolvedExpenseKey).map((r) => r.key) ?? [];
      const reportsToRemove = oldReports.filter((r) => !reportKeys.includes(r));

      updateReportsEntities({
        reportKeys,
        entityType: 'expenses',
        entityKeys: [resolvedExpenseKey],
      });

      updateReportsEntities({
        reportKeys: reportsToRemove,
        entityType: 'expenses',
        entityKeys: [resolvedExpenseKey],
        remove: true,
      });

      return true;
    } catch (e) {
      alert(getTranslation('Error saving expense. Please contact support.'));
      return false;
    }
  }, [expense, newVendorName, reportKeys]);

  const goToExpenseAtIndex = React.useCallback(
    (index: number) => {
      const nextKey = navOrder[index];
      if (!nextKey || nextKey === expenseKey) {
        return;
      }
      // Arrow navigation replaces the history entry, so save any edits before
      // leaving rather than dropping them. If the save fails validation, stay
      // put so the user can see and fix the errors.
      if (hasUnsavedChanges && !persistExpense()) {
        return;
      }
      // Carry returnTo along so closing after paging still returns to origin.
      navigate(`/expense/${nextKey}`, { replace: true, state: { returnTo } });
    },
    [navOrder, expenseKey, navigate, hasUnsavedChanges, persistExpense, returnTo],
  );

  const goToPreviousExpense = React.useCallback(() => {
    if (currentNavIndex > 0) {
      goToExpenseAtIndex(currentNavIndex - 1);
    }
  }, [currentNavIndex, goToExpenseAtIndex]);

  const goToNextExpense = React.useCallback(() => {
    if (currentNavIndex >= 0 && currentNavIndex < navOrder.length - 1) {
      goToExpenseAtIndex(currentNavIndex + 1);
    }
  }, [currentNavIndex, navOrder.length, goToExpenseAtIndex]);

  // Up/Down arrows step to the previous/next expense in the visible list order.
  React.useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      // Stand down while typing, while the split modal is open, or while a
      // date-picker / dialog owns the keyboard, so those controls keep the keys.
      if (isEditableTarget(event.target) || showSplitModal || isBlockingOverlayOpen()) {
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        goToPreviousExpense();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        goToNextExpense();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goToPreviousExpense, goToNextExpense, showSplitModal]);

  // Re-mount keyed form fields, reset the receipt viewer, and drop any pending
  // new vendor name when switching to a different expense via the nav arrows
  // (the route stays mounted, only the expenseKey param changes).
  React.useEffect(() => {
    setRenderKey(cuid());
    setSelectedReceiptIndex(0);
    setNewVendorName(undefined);
  }, [expenseKey]);

  React.useEffect(() => {
    if (expenseKey) {
      if (!loadedExpense) {
        expenseKeyRef.current = undefined;
      } else {
        setExpense(loadedExpense);
        expenseKeyRef.current = expenseKey;
      }
    }
  }, [loadedExpense]);

  function onMobile() {
    if (screenWidth < 768) {
      if (
        window.confirm(
          getTranslation(
            'The Experience is better on the App! Press "OK" to download the app. "CANCEL" will load this website',
          ),
        )
      ) {
        window.location.href = 'https://app.easy-expense.com/app-download';
      }
    }
  }

  React.useEffect(() => {
    onMobile();
  }, []);

  function onSave() {
    if (persistExpense()) {
      navigate(returnTo ?? `/transactions`);
    }
  }

  function onDelete() {
    passResumeRowToNeighbour();
    if (expenseKeyRef.current) {
      Data.expenses.archive(expenseKeyRef.current, undefined, undefined, 'details');
    }
    navigate(returnTo ?? `/transactions`);
  }

  function isNewExpense() {
    return !expenseKeyRef.current;
  }

  function handleSplitExpense(values: SplitExpenseFormValues) {
    if (!values || !values.splits || values.splits.length < 2) {
      return;
    }

    // "parent", not "original": on an expense with a foreign currency, `original*` means the
    // amount as it appeared on the receipt, which is a different thing entirely.
    const parentTotal = expense.total ?? 0;
    const parentTax = expense.tax ?? 0;
    const parentSecondTax = expense.secondTax ?? 0;
    const parentOriginalTotal = expense.originalTotal ?? 0;
    const parentOriginalTax = expense.originalTax ?? 0;
    const parentOriginalSecondTax = expense.originalSecondTax ?? 0;
    const parentTip = expense.tip ?? 0;

    // Filter out splits with invalid totals or missing categories
    const validSplits = values.splits.filter(
      (split) =>
        split.total !== undefined &&
        split.total !== null &&
        !isNaN(split.total) &&
        split.total > 0 &&
        split.category,
    );

    if (validSplits.length < 2) {
      alert(getTranslation('Please ensure all splits have valid amounts and categories.'));
      return;
    }

    // Approval state is never carried over from the form state: it goes stale as soon as a manager
    // reviews the expense while this screen is open. Writing a stale value back would be denied
    // outright for the base row, whose update may only keep the stored values
    // (`memberApprovalTransitionAllowed` in firestore.rules). Omitting the fields lets the store
    // merge keep what's actually stored.
    const inheritedValues = _.omit(expense, [
      'approvalStatus',
      'rejection',
      'approvalSource',
      'reimbursementSource',
    ]);

    // The base row is an update of the original, so the only status change the rules permit is the
    // creator resubmitting after a rejection.
    const baseApprovalFields: Partial<Expense> = {};
    if (loadedExpense?.approvalStatus === 'rejected') {
      // The member just changed what the manager rejected, so put it back in the queue instead of
      // leaving a stale rejection reason against a now-different total.
      baseApprovalFields.approvalStatus = 'pending';
      baseApprovalFields.rejection = null;
    }

    // Bank-ingested expenses store a null `reimbursableState`, which `isEligibleForReimburseCascade`
    // doesn't accept — normalize so the base row isn't skipped by a report's reimburse cascade while
    // its split-off siblings are included. An already-reimbursed expense is left alone: a member may
    // not clear `reimbursementSource`, so flipping the state would contradict it.
    const isReimbursed =
      loadedExpense?.reimbursableState === 'reimbursed' || !!loadedExpense?.reimbursementSource;
    if (!isReimbursed) {
      baseApprovalFields.reimbursableState = 'notReimbursed';
    }

    const splitApprovalFields = getSplitApprovalFields(loadedExpense, isMember);

    try {
      validSplits.forEach((split, index) => {
        // split.total is guaranteed to be defined and > 0 due to validSplits filter
        const splitTotal = split.total!;
        // Calculate proportional taxes (avoid division by zero)
        const splitTax =
          parentTotal > 0 ? Math.round(((parentTax * splitTotal) / parentTotal) * 100) / 100 : 0;
        const splitSecondTax =
          parentTotal > 0
            ? Math.round(((parentSecondTax * splitTotal) / parentTotal) * 100) / 100
            : 0;
        // Receipt amounts are prorated on the same ratio as the total, so each split keeps the
        // parent's Effective Rate and the receipt's amounts still add back up across the splits.
        function prorateOriginal(parentOriginal: number) {
          if (parentOriginal > 0 && parentTotal > 0) {
            return roundToCents((parentOriginal * splitTotal) / parentTotal);
          }
          return undefined;
        }
        const splitOriginalTotal = prorateOriginal(parentOriginalTotal);
        const splitOriginalTax = prorateOriginal(parentOriginalTax);
        const splitOriginalSecondTax = prorateOriginal(parentOriginalSecondTax);
        const splitTip =
          parentTotal > 0 ? Math.round(((parentTip * splitTotal) / parentTotal) * 100) / 100 : 0;

        const splitExpenseData: Partial<Expense> = {
          ...inheritedValues,
          category: split.category,
          total: splitTotal,
          tax: splitTax,
          secondTax: splitSecondTax,
          originalTotal: splitOriginalTotal,
          originalTax: splitOriginalTax,
          originalSecondTax: splitOriginalSecondTax,
          tip: splitTip || null,
          receipts: expense.receipts ?? [], // All splits get the same receipts (cloud URLs)
        };

        try {
          // Handle first split (update existing or create new expense)
          if (index === 0 && expenseKeyRef.current) {
            Data.expenses.update(
              expenseKeyRef.current,
              { ...splitExpenseData, ...baseApprovalFields },
              undefined,
              undefined,
              'details',
            );
          } else {
            const createdByKey = loadedExpense?.createdBy ?? currentUser()?.uid;
            // Create new expense for first split (if new) or remaining splits
            if (!createdByKey) {
              throw new Error(getTranslation('No createdBy found'));
            }
            const createdKey = Data.expenses.create(
              {
                ...splitExpenseData,
                ...splitApprovalFields,
                ...entityFields('create', createdByKey),
                ...(index > 0 ? { key: cuid() } : {}),
              },
              undefined,
              undefined,
              'details',
            );
            if (index === 0) {
              expenseKeyRef.current = createdKey;
            }
          }
        } catch (splitError) {
          console.error(`Error processing split ${index}:`, splitError);
          throw splitError; // Re-throw to be caught by outer try-catch
        }
      });

      navigate(returnTo ?? `/transactions`);
    } catch (e) {
      console.error('Error splitting expense:', e);
      alert(getTranslation('Error splitting expense. Please contact support.'));
    }
  }

  function getVendorName(): string {
    if (expense.vendor) {
      // A deleted vendor is out of the selectable list, but the expense still points at it.
      const vendor =
        allVendors.find((v) => v.key === expense.vendor) ?? Data.vendors.getByKey(expense.vendor);
      return vendor?.value?.name ?? '';
    }
    return '';
  }

  return (
    <Layout.Column style={{ width: '100%', height: '100%' }}>
      {loading ? (
        <Layout.Column
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'fixed',
            zIndex: zIndex.LoadingOverlay,
          }}
        >
          <LoadingSpinner />
        </Layout.Column>
      ) : null}
      <Layout.Row style={{ height: '100%', width: '100%' }}>
        {screenWidth > 800 ? (
          <Layout.Column
            style={{ minWidth: 600, maxWidth: 800, height: '100vh' }}
            grow
            className="hide-on-mobile"
          >
            <ReceiptViewer
              receipts={expense.receipts ?? []}
              onChange={(receipts: string[]) => {
                setExpense((expense) => ({ ...expense, receipts }));
              }}
              selectedReceiptIndex={selectedReceiptIndex}
              setSelectedReceiptIndex={setSelectedReceiptIndex}
              setLoading={setLoading}
              isNewExpense={isNewExpense()}
              locked={isLockedForMember}
              setExpense={(expense) => {
                setExpense(expense);
                setRenderKey(cuid()); // causes a rerender of all children
              }}
            />
          </Layout.Column>
        ) : null}

        <Layout.Column
          grow
          style={{
            height: '100vh',
            boxShadow: '-8px 0 8px rgba(31, 31, 31, 0.1)',
          }}
        >
          <Layout.Column style={{ overflow: 'scroll' }} grow py={24} px={32}>
            <Layout.Row>
              <Spacer.Flex style={{ maxWidth: 150 }} />
              <Layout.Column
                style={{
                  width: 700,
                  pointerEvents: isLockedForMember ? 'none' : 'auto',
                  opacity: isLockedForMember ? 0.6 : 1,
                }}
              >
                <Spacer.Vertical size={32} />

                {expense.approvalStatus && expenseKey ? (
                  <>
                    <ApprovalStatusSection
                      approvalStatus={expense.approvalStatus}
                      rejection={expense.rejection}
                      approvalSource={expense.approvalSource}
                      reimbursementSource={expense.reimbursementSource}
                      isReimbursable={expense.isReimbursable}
                      reimbursableState={expense.reimbursableState}
                      total={expense.total}
                      createdBy={expense.createdBy}
                      paymentMethod={expense.paymentMethod}
                      expenseKey={expenseKey}
                      setLoading={setLoading}
                      onUpdate={(patch) => setExpense((prev) => ({ ...prev, ...patch }))}
                    />
                    <Spacer.Vertical size={'s-16'} />
                  </>
                ) : null}

                <SearchDropdown
                  header={getTranslation('Vendor')}
                  placeholder={getTranslation('Vendor Name')}
                  selectedKey={expense.vendor}
                  autoCompleteValues={allVendors}
                  onSelect={(v) => {
                    if (v?.key) {
                      const existingVendor = allVendors.find((nv) => nv.key === v.key);
                      if (existingVendor) {
                        const vendorCategory = existingVendor.value.category;
                        setExpense((expense) => ({
                          ...expense,
                          vendor: v?.key,
                          category: vendorCategory || expense.category,
                        }));
                        setNewVendorName(undefined);
                      } else {
                        setNewVendorName(v?.value?.name);
                      }
                    }
                  }}
                  error={!!errors?.vendor}
                  iconicIcon="storefront-outline"
                  clearOnSelect={true}
                  allowCreateNew={true}
                />

                <Spacer.Vertical size={'s-16'} />

                <SearchDropdown
                  header={getTranslation('Category')}
                  placeholder={getTranslation('Category Name')}
                  selectedKey={expense.category}
                  autoCompleteValues={categoryOptions}
                  onSelect={(c) => {
                    if (c?.key) {
                      const tipEligible = isTipEligibleCategoryKey(c.key);
                      setExpense((expense) => ({
                        ...expense,
                        category: c?.key,
                        tip: tipEligible ? expense.tip : null,
                      }));
                    }
                  }}
                  error={!!errors?.category}
                  iconicIcon="pricetags-outline"
                  clearOnSelect={true}
                />

                {expense.total && expense.total > 0 ? (
                  <Layout.PressableRow
                    onClick={() => {
                      setShowSplitModal(true);
                    }}
                    style={{ marginTop: 8, cursor: 'pointer' }}
                    bg="transparent"
                  >
                    <Icon name="git-merge-outline" size={16} color={theme.colors.brandPrimary} />
                    <Spacer.Horizontal size={6} />
                    <OpenSans.Primary
                      size="xs-12"
                      style={{ color: theme.colors.brandPrimary }}
                      weight="bold-700"
                    >
                      {getTranslation('Split receipt to multiple categories')}
                    </OpenSans.Primary>
                  </Layout.PressableRow>
                ) : null}

                <Spacer.Vertical size={'s-16'} />

                <ExpenseTotalField
                  key={`expenseTotal_${renderKey}`}
                  loadedExpense={loadedExpense}
                  expense={expense}
                  renderKey={renderKey}
                  errors={errors}
                  setExpense={setExpense}
                />

                {showTipField ? (
                  <>
                    <Spacer.Vertical size={'s-16'} />
                    <TipField
                      tip={expense.tip}
                      renderKey={renderKey}
                      onChange={(tip) => setExpense((expense) => ({ ...expense, tip }))}
                    />
                  </>
                ) : null}

                <Spacer.Vertical size={'s-16'} />

                <PaymentMethodField
                  key={`paymentMethod_${renderKey}`}
                  initialPaymentMethodKey={expense.paymentMethod}
                  onChange={(paymentMethodKey) =>
                    setExpense((expense) => ({ ...expense, paymentMethod: paymentMethodKey }))
                  }
                />

                <Spacer.Vertical size={'s-16'} />

                <DateField
                  key={`date_${renderKey}`}
                  error={!!errors?.date}
                  initialDate={expense.date}
                  disabled={loading}
                  onChange={(date) => setExpense((expense) => ({ ...expense, date }))}
                />

                {workspaceSettings?.showTransactionID ? (
                  <>
                    <Spacer.Vertical size={'s-16'} />

                    <TransactionNumberField
                      key={`transactionID_${renderKey}`}
                      transactionID={expense.transactionID}
                      onChange={(transactionID) =>
                        setExpense((expense) => ({ ...expense, transactionID }))
                      }
                    />
                  </>
                ) : null}

                <Spacer.Vertical size={'s-16'} />

                <ReportsField
                  expenseKey={expense.key}
                  onChange={(rks) => _setReportKeys([...rks])}
                />

                {workspaceSettings?.showReimbursementsToggle ? (
                  <Layout.Column>
                    <Spacer.Vertical size={16} />

                    <OpenSans.Secondary size={12} weight="bold-700">
                      {getTranslation('Reimburse')}
                    </OpenSans.Secondary>

                    <Spacer.Vertical size={4} />

                    <Layout.Column
                      bg="inputBackground"
                      px
                      py
                      radius={8}
                      border={[1, 'solid', 'inputBorder']}
                    >
                      <Layout.Row align>
                        <Checkbox
                          style={{ padding: 0 }}
                          checked={!!expense.isReimbursable}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setExpense((expense) => ({
                              ...expense,
                              isReimbursable: event.target.checked,
                              reimbursableState: event.target.checked ? 'notReimbursed' : null,
                            }));
                          }}
                        />

                        <Spacer.Horizontal size={12} />

                        <OpenSans.Primary
                          size="s"
                          weight={expense.isReimbursable ? 'medium' : 'light'}
                        >
                          {getTranslation('Needs Reimbursement')}
                        </OpenSans.Primary>
                      </Layout.Row>

                      <Spacer.Vertical size={16} />

                      <Layout.Row align>
                        <Checkbox
                          style={{ padding: 0 }}
                          disabled={isMember}
                          checked={expense.reimbursableState === 'reimbursed'}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setExpense((expense) => ({
                              ...expense,
                              reimbursableState: event.target.checked
                                ? 'reimbursed'
                                : 'notReimbursed',
                              isReimbursable: event.target.checked ? true : expense.isReimbursable,
                            }));
                          }}
                        />

                        <Spacer.Horizontal size={12} />

                        <OpenSans.Primary
                          size={16}
                          weight={expense.reimbursableState === 'reimbursed' ? 'medium' : 'light'}
                        >
                          {getTranslation('Has Been Reimbursed')}
                        </OpenSans.Primary>
                      </Layout.Row>
                    </Layout.Column>
                  </Layout.Column>
                ) : null}

                <Spacer.Vertical size={'s-16'} />

                <NotesField
                  key={`notes_${renderKey}`}
                  desc={expense.desc}
                  onChange={(notes) => setExpense((expense) => ({ ...expense, desc: notes }))}
                />

                <Spacer.Vertical size={'s-16'} />

                <ReceiptsField
                  receipts={expense.receipts ?? []}
                  onChange={(receipts) => setExpense((expense) => ({ ...expense, receipts }))}
                  selectedReceiptIndex={selectedReceiptIndex}
                  setSelectedReceiptIndex={setSelectedReceiptIndex}
                  setLoading={setLoading}
                  isNewExpense={isNewExpense()}
                  setExpense={setExpense}
                />

                <Spacer.Vertical size={100} />
              </Layout.Column>
              <Spacer.Flex />
            </Layout.Row>
          </Layout.Column>

          <Layout.Row
            style={{
              borderTop: `2px solid ${theme.colors.grayXLight}`,
            }}
            bg="white"
            px={32}
            py={24}
          >
            {isLockedForMember ? (
              <Layout.Row align>
                <Icon name="lock-closed-outline" size={20} color={theme.colors.secondary} />
                <Spacer.Horizontal size={8} />
                <OpenSans.Secondary size="s-16" weight="bold-700">
                  {getTranslation(
                    'This expense is locked and can be changed by a manager or admin',
                  )}
                </OpenSans.Secondary>
              </Layout.Row>
            ) : (
              <>
                <Layout.PressableRow
                  px
                  py={8}
                  bg="destructiveXLight"
                  radius={100}
                  border={[2, 'solid', 'white']}
                  align
                  onClick={onDelete}
                >
                  <Icon name="trash-outline" size={20} color={theme.colors.destructiveDark} />
                  <Spacer.Horizontal size={8} />
                  <OpenSans.Primary color="destructiveDark" grow size="s-16">
                    {getTranslation('Delete Expense')}
                  </OpenSans.Primary>
                  <Spacer.Horizontal size={4} />
                </Layout.PressableRow>

                <Spacer.Horizontal size={'m-18'} />

                <Button.Primary
                  onClick={onSave}
                  radius={50}
                  style={{
                    border: '2px solid #fff',
                    boxShadow: '0 0 8px rgba(255, 255, 255, .5)',
                  }}
                >
                  <OpenSans.Custom
                    size={15}
                    weight="bold-700"
                    style={{ color: theme.colors.buttonWhite }}
                  >
                    {getTranslation('Save Expense')}
                  </OpenSans.Custom>
                  <Icon
                    size={15}
                    color={theme.colors.buttonWhite}
                    style={{ paddingLeft: 10 }}
                    name="chevron-forward"
                  />
                </Button.Primary>
              </>
            )}
          </Layout.Row>
        </Layout.Column>
      </Layout.Row>

      <CloseButton onClose={() => (returnTo ? navigate(returnTo) : navigate(-1))} />

      {showSplitModal ? null : (
        <ExpenseNavControl
          currentIndex={currentNavIndex}
          total={navOrder.length}
          onPrevious={goToPreviousExpense}
          onNext={goToNextExpense}
        />
      )}

      <SplitExpenseModal
        showModal={showSplitModal}
        setShowModal={setShowSplitModal}
        expense={expense}
        vendor={getVendorName()}
        total={expense.total ?? 0}
        onSplit={handleSplitExpense}
      />
    </Layout.Column>
  );
};
